import React, { useState } from 'react';
import styled from 'styled-components';
import { Button, Input, Text } from '@teals/ui-web';
import { useAuth } from '../../hooks';

type AuthView = 'signIn' | 'signUp' | 'verify' | 'forgotPassword' | 'resetPassword';

const Container = styled.div`
  width: 100%;
  max-width: 380px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const TabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${({ $active }) => ($active ? 'var(--accent)' : 'var(--bg-elevated)')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--text-secondary)')};

  &:hover {
    background: ${({ $active }) => ($active ? 'var(--accent-hover)' : 'var(--bg-inset)')};
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ErrorMsg = styled.div`
  padding: 0.75rem 1rem;
  background: var(--error-subtle);
  color: var(--error);
  border-radius: 8px;
  font-size: 0.875rem;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  cursor: pointer;
  text-align: left;
  padding: 0;

  &:hover {
    color: var(--accent-hover);
  }
`;

const Auth: React.FC = () => {
  const { login, signup, confirmSignup, resendCode, isLoading, error } = useAuth();
  const [view, setView] = useState<AuthView>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    await login(email, password);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await signup(email, password, name);
    if (result) {
      setView('verify');
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    await confirmSignup(email, code, password);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const { forgotPassword } = await import('../../services/cognito');
    try {
      await forgotPassword(email);
      setView('resetPassword');
    } catch {
      // error handled by cognito service
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const { confirmNewPassword } = await import('../../services/cognito');
    try {
      await confirmNewPassword(email, code, password);
      setView('signIn');
    } catch {
      // error handled by cognito service
    }
  };

  if (view === 'verify') {
    return (
      <Container>
        <Text variant="h3" style={{ marginBottom: '0.5rem' }}>Verify your email</Text>
        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          We sent a verification code to <strong>{email}</strong>
        </p>
        {error && <ErrorMsg>{error}</ErrorMsg>}
        <Form onSubmit={handleVerify}>
          <Input
            label="Verification code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter 6-digit code"
          />
          <Button type="submit" fullWidth isLoading={isLoading}>
            Verify & Sign In
          </Button>
          <LinkButton type="button" onClick={() => resendCode(email)}>
            Resend code
          </LinkButton>
        </Form>
      </Container>
    );
  }

  if (view === 'forgotPassword') {
    return (
      <Container>
        <Text variant="h3" style={{ marginBottom: '0.5rem' }}>Reset password</Text>
        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Enter your email and we'll send a reset code.
        </p>
        <Form onSubmit={handleForgotPassword}>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <Button type="submit" fullWidth isLoading={isLoading}>
            Send Reset Code
          </Button>
          <LinkButton type="button" onClick={() => setView('signIn')}>
            Back to sign in
          </LinkButton>
        </Form>
      </Container>
    );
  }

  if (view === 'resetPassword') {
    return (
      <Container>
        <Text variant="h3" style={{ marginBottom: '0.5rem' }}>New password</Text>
        <p style={{ marginBottom: '1.5rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Enter the code sent to <strong>{email}</strong> and your new password.
        </p>
        <Form onSubmit={handleResetPassword}>
          <Input
            label="Reset code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Enter code"
          />
          <Input
            label="New password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="New password"
          />
          <Button type="submit" fullWidth isLoading={isLoading}>
            Reset Password
          </Button>
          <LinkButton type="button" onClick={() => setView('signIn')}>
            Back to sign in
          </LinkButton>
        </Form>
      </Container>
    );
  }

  return (
    <Container>
      <TabRow>
        <TabButton $active={view === 'signIn'} onClick={() => setView('signIn')}>
          Sign In
        </TabButton>
        <TabButton $active={view === 'signUp'} onClick={() => setView('signUp')}>
          Sign Up
        </TabButton>
      </TabRow>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {view === 'signIn' ? (
        <Form onSubmit={handleSignIn}>
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter password"
          />
          <LinkButton type="button" onClick={() => setView('forgotPassword')}>
            Forgot password?
          </LinkButton>
          <Button type="submit" fullWidth isLoading={isLoading}>
            Sign In
          </Button>
        </Form>
      ) : (
        <Form onSubmit={handleSignUp}>
          <Input
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
          <Input
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com"
          />
          <Input
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Create a password"
          />
          <Button type="submit" fullWidth isLoading={isLoading}>
            Create Account
          </Button>
        </Form>
      )}
    </Container>
  );
};

export default Auth;
